import {Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject} from "@angular/core";
import {ActivatedRoute, Event as RouterEvent, NavigationEnd, Router} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NavigationItem} from "../../../../features/core/shared/dto/navigation/navigation-item";
import {MvsObjectNavigationService} from "../../../../features/core/shared/navigation/mvs-object-navigation.service";
import {
    UiHomeScreenNavigationService
} from "../../../../features/core/ui/service/domain/ui-home-screen-navigation.service";
import {UiHomeScreenInfoDto} from "../../../../features/core/ui/model/dto/api/ui-home-screen-info.dto";
import {MenuItem} from "./app.menuitem.component";


@Component({
    selector: 'app-navigation-items',
    template: `<div class="menu-wrapper" *ngIf="navigationItems">
    <div class="layout-menu-container">

        <!--
          Alpha Home - the user's own start pages, one FLAT entry each.

          Not a foldable group, and that is a measured decision rather than a style choice. It WAS
          a group ([root]="false", children behind 'item.items'), and the children never appeared:
          'AppMenuitemComponent' renders a submenu only when 'root || active', and in slim mode -
          which this shell runs in by default - 'active' is FORCED to false on every NavigationEnd.
          Slim mode is meant to show submenus as a flyout through the 'root' branch instead; that
          path renders the children at 0x0 px, hover or click, so they are in the DOM and
          unreachable. Measured both ways, screenshots in the review.

          The reason it was never noticed is that NO OTHER ENTRY IN THIS APPLICATION HAS CHILDREN -
          all 25 entries of a config page are leaves - so the slim flyout has never been exercised
          here and is not styled. Flat entries are the shape this menu actually supports: each
          start page becomes its own icon in the rail, exactly like the config entries above.

          Still NOT part of 'navigationItems': those are the TABS OF THE PAGE behind the menu and
          are answered with a '?navItem=' on the URL, while these are routes. Mixing the two would
          send a screen switch through 'onMenuClick' and put a tab name on the launchpad's address.

          ONLY ON THE START PAGE ITSELF - 'onLaunchpad'. This menu column stands on EVERY page of
          the application, so a condition on the list alone put the start pages into the rail of
          every config page, of cr, of tm, everywhere. They are the switch BETWEEN start pages and
          have nothing to say anywhere else; the way TO the start page is a tile and the logo, not
          this block. See 'onLaunchpad' for why the match is exact and not a prefix.
        -->
        <ul class="layout-menu mt-3" *ngIf="onLaunchpad && homeScreenItems.length">
            <li class="mb-3" app-menuitem
                *ngFor="let screenItem of homeScreenItems; let i = index"
                [item]="screenItem"
                [index]="i"
                [parentKey]="'ui-home-screens'"
                [root]="true">
            </li>
        </ul>
        <p-divider styleClass="border-top-1 border-400 mt-2 mb-4"
                   *ngIf="onLaunchpad && homeScreenItems.length && navigationItems.length"></p-divider>

        <ul class="layout-menu mt-3">
            <ng-container *ngFor="let item of navigationItems;  let i = index;">
                <li class="mb-3" app-menuitem *ngIf="!item.divider"
                    [item]="item"
                    [index]="i"
                    [ngClass]="item.action == selectedNavItem ? 'bg-primary-50 active-side-nav' : ''"
                    [root]="true"
                    (click)="onMenuClick(item)">
                </li>
                <p-divider styleClass="border-top-1 border-400 mt-2 mb-4" *ngIf="item.divider"></p-divider>
            </ng-container>
        </ul>
    </div>
</div>`,
    standalone: false
})
export class AppNavigationItemComponent implements OnInit, OnChanges {

    @Input() navigationItems: NavigationItem[];
    @Input() selectedNavItem: string;
    @Output() selectedNavItemChange = new EventEmitter<string>();

    initialized: boolean = false;

    /**
     * One flat menu entry per start page, then "Neue Startseite". Empty while nothing is known.
     *
     * Held as a FIELD and rebuilt only when the list or the current screen changes. Built in a
     * getter it would be a new array on every change detection run, which is a changed
     * {@code @Input} to every {@code app-menuitem} every time.
     */
    homeScreenItems: MenuItem[] = [];

    /**
     * The start page is what is on screen - the one place the entries above belong.
     *
     * Held as a FIELD for the same reason as {@link homeScreenItems}: in a getter this would be
     * recomputed on every change detection run, and it changes only on a navigation.
     *
     * <h3>Why the comparison is exact and not a prefix</h3>
     * A plain {@code startsWith('/launchpad')} also matches {@code /launchpadfoo} - any future
     * route whose first segment merely BEGINS with the word. So the two forms the launchpad
     * actually has are named: the bare {@code /launchpad}, which the server answers with the
     * default screen, and {@code /launchpad/{id}}, which names one. Query string and fragment are
     * cut off first, because {@code Router.url} carries both - {@code /launchpad?x=1} is the start
     * page and would fail a bare equality test.
     *
     * <h3>Why not simply the base href</h3>
     * The shell runs on {@code HashLocationStrategy} (see {@code app.module.ts}), so the address
     * reads {@code /#/launchpad}. That {@code #} belongs to the strategy and not to the route:
     * {@code HashLocationStrategy.path()} hands the router the hash WITHOUT its leading {@code #},
     * and {@code Router.url} is the serialized url tree built from it. What arrives here is
     * therefore {@code /launchpad}, never {@code /#/launchpad}.
     */
    onLaunchpad: boolean = false;

    /** The route of the personal start page - see {@link onLaunchpad} for the exact match. */
    private static readonly LAUNCHPAD_PATH = '/launchpad';

    protected destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        public router: Router,
        private activatedRoute: ActivatedRoute,
        private navigationService: MvsObjectNavigationService,
        private screenNavigation: UiHomeScreenNavigationService) {
    }

    ngOnInit(): void {
        this.init();
        this.subscribeLaunchpadRoute();
        this.subscribeHomeScreens();
    }

    init(){
        this.refreshComponent();
    }

    refreshComponent(){
        this.initialized = true;
    }

    ngOnChanges(changes:SimpleChanges): void {
        if(!this.initialized){
            return ;
        }

        if(changes['navigationItems']){
            this.refreshComponent();
        }

    }

    /**
     * Keeps {@link onLaunchpad} in step with the address.
     *
     * Read ONCE from {@code router.url} and then followed on every {@code NavigationEnd}. Both
     * halves are needed and neither covers the other:
     * <ul>
     *   <li>the initial read, because this component is usually built DURING a navigation - the
     *       one that brought the page up - and would otherwise wait for the next one;</li>
     *   <li>the subscription, because the component is NOT always rebuilt on a page change. The
     *       javadoc of {@link subscribeHomeScreens} says it is, and for a change of page that
     *       holds; a switch from {@code /launchpad} to {@code /launchpad/5} is precisely the case
     *       where the router reuses what is already there.</li>
     * </ul>
     *
     * {@code urlAfterRedirects} rather than {@code url}: a redirected navigation reports the
     * address it STARTED at in {@code url}, which is not where the user ended up.
     */
    private subscribeLaunchpadRoute(): void {

        this.applyLaunchpadRoute(this.router.url);

        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: RouterEvent) => {

                if (event instanceof NavigationEnd) {
                    this.applyLaunchpadRoute(event.urlAfterRedirects);
                }
            });
    }

    private applyLaunchpadRoute(url: string): void {
        this.onLaunchpad = AppNavigationItemComponent.isLaunchpad(url);
    }

    /**
     * Whether an address is the personal start page - exactly, never by prefix.
     *
     * Query string and fragment are cut off first: {@code Router.url} serializes both, and
     * {@code /launchpad?tab=1} is the start page. What is left is compared against the route in
     * its two forms - see {@link onLaunchpad} for why {@code startsWith} alone is wrong.
     *
     * @param url an address as the router serializes it, e.g. {@code /launchpad/5}
     */
    private static isLaunchpad(url: string): boolean {

        const path = (url ?? '').split('?')[0].split('#')[0];

        return path === AppNavigationItemComponent.LAUNCHPAD_PATH
            || path.startsWith(`${AppNavigationItemComponent.LAUNCHPAD_PATH}/`);
    }

    /**
     * Keeps the start-page entry in step with the list and with the screen on display.
     *
     * This component is torn down and rebuilt on every page change - {@code AppMainComponent}
     * clears {@code navigationItems} on each {@code pageChange} - so the list itself is held by
     * {@link UiHomeScreenNavigationService} and only READ here. {@code ensureLoaded} is therefore
     * cheap to call on every rebuild: it issues a request only the first time.
     */
    private subscribeHomeScreens(): void {

        this.screenNavigation.screens$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((screens: UiHomeScreenInfoDto[]) => this.buildHomeScreenItems(screens));

        this.screenNavigation.currentScreenId$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.buildHomeScreenItems(this.screenNavigation.screens));

        this.screenNavigation.ensureLoaded();
    }

    /**
     * Builds one entry per start page, then "Neue Startseite".
     *
     * <h3>Why flat and not a group</h3>
     * See the template comment: the group form renders no reachable children in this shell, and
     * the reason is that no other entry in the application has children at all, so the slim-mode
     * flyout has never been styled. A flat entry is what this menu supports, and it is also what
     * the config pages beside it look like - a column of icons in the rail, each with a tooltip.
     *
     * The cost is the grouping and its heading. What is bought is a start page that can actually
     * be switched to.
     *
     * <h3>Every entry needs its own icon</h3>
     * In the collapsed rail the icon is ALL there is - the label is hidden and comes back only as
     * a tooltip on hover. Two screens without icons would be two identical dots. The fallback is
     * therefore a neutral dot and not a guess, and choosing an icon is offered in the management
     * dialog for exactly this reason.
     *
     * <b>The icon may be {@code null}</b> - the server sends the key with a {@code null} value for
     * a screen that has none, so a test for {@code undefined} would miss it. Falsiness covers both.
     */
    private buildHomeScreenItems(screens: UiHomeScreenInfoDto[]): void {

        const currentScreenId = this.screenNavigation.currentScreenId;

        const items: MenuItem[] = (screens ?? []).map((screen: UiHomeScreenInfoDto) => ({
            label: screen.name,
            icon: screen.icon || 'pi pi-fw pi-circle-fill',
            routerLink: ['/launchpad', String(screen.id)],

            // Marks the screen on display even when the address carries no id at all: the
            // launchpad reports the screen it was ANSWERED with, which for the parameterless
            // form is the default one. 'routerLinkActive' cannot know that - it compares
            // addresses, and '/launchpad' is not '/launchpad/5'.
            class: screen.id === currentScreenId ? 'active-menuitem-routerlink' : null,
        }));

        items.push({
            label: 'Neue Startseite',
            icon: 'pi pi-fw pi-plus',
            command: () => this.openHomeScreenManagement(),
        });

        this.homeScreenItems = items;
    }

    /**
     * Opens the management of the start pages, with the proposed name already in its field.
     *
     * <h3>What this entry used to do, and why it stopped</h3>
     * It created a start page in one press, under a generated name ("Neue Startseite 2"), and
     * went to it. That was right while there was no dialog - a working entry beats a button that
     * does nothing - and it is wrong now that there is one: the user learned the name of their
     * new page only afterwards, and renaming it was a second errand.
     *
     * The label stays. It says what the user wants, and it is the heading of the first block of
     * the dialog it now opens, where the proposed name is filled in and selected. One Enter gives
     * the old outcome; anything else gives a name and an icon the user chose.
     *
     * <h3>Why it goes through the service and not straight to the dialog</h3>
     * The dialog lives on the launchpad and this menu does not - it is on every page of the
     * application. So the request is left with {@code UiHomeScreenNavigationService}, which both
     * sides already share, and the route follows. A launchpad that is already on screen hears the
     * request; one that is still being built finds it waiting.
     *
     * Nothing can fail here any more, so there is no error path and no {@code busy}: the press
     * opens a dialog, and the dialog does the asking, the refusing and the reporting.
     */
    private openHomeScreenManagement(): void {

        this.screenNavigation.requestManagement();
        this.router.navigate(['/launchpad']);
    }

    onMenuClick(event: NavigationItem) {

        if (event.customFunction){
            event.customFunction();
            return;
        }

        // TM-1000001554: While an object-navigation overlay is active, tab state
        // lives on `MvsObjectNavigationService.overlayNavItem$` (see
        // `CrCustomerObjectPage` and `ObjectBaseModeComponent`). The URL's
        // `navItem` belongs to the MAIN page behind the overlay and must stay
        // untouched so the main page can be restored to its original tab when
        // the overlay closes. Route all overlay-tab writes through the service.
        const overlayActive = !!this.navigationService?.getActiveOverlay();

        if (this.selectedNavItem == event.action) {
            //same nav item is selected

            if (event.toggleable == false) {
                //same nav item is selected and nav item can not be toggled if we click again so we do nothing
                return;
            }

            // otherwise nav item can be toggled
            this.selectedNavItem = null;

            if (overlayActive) {
                this.navigationService.setOverlayNavItem(null);
                this.selectedNavItemChange.emit(this.selectedNavItem);
                return;
            }

            const queryParams = { ...this.activatedRoute.snapshot.queryParams };
            delete queryParams['navItem'];
            this.removeNavItemParam(queryParams);
            this.selectedNavItemChange.emit(this.selectedNavItem);
            return;
        }

        // different nav item is selected

        this.selectedNavItem = event.action;

        if (overlayActive) {
            this.navigationService.setOverlayNavItem(this.selectedNavItem);
            this.selectedNavItemChange.emit(this.selectedNavItem);
            return;
        }

        const queryParams = {};
        queryParams['navItem'] =  this.selectedNavItem;
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
            queryParamsHandling: 'merge'
        });
        this.selectedNavItemChange.emit(this.selectedNavItem);
    }

    removeNavItemParam(queryParams: object) {
        this.router.navigate([], { queryParams: queryParams });
    }




}
